import { Button } from '@appd/uik';
import { Editor, Monaco } from '@monaco-editor/react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { jsonataWithCallback } from '../../../modules';
import { ExpressionEditor } from '../DashifyEditorConstants';

interface EditorExpressionsProps {
  data: Record<string, unknown>;
  expression: unknown;
  isFullInspectMode: boolean;
  setDataSources: Dispatch<SetStateAction<string[]>>;
  setFullInspectMode: (isFullInspectMode: boolean) => void;
  onSave: (expr: string, key?: string) => void;
}

export const DashifyEditorExpressions: React.FC<EditorExpressionsProps> = ({
  data,
  isFullInspectMode,
  setDataSources,
  setFullInspectMode,
  onSave,
}) => {
  const json = JSON.stringify(data, null, 2);
  const [expression, setExpression] = useState('');
  const [result, setResult] = useState<string>();
  const [isPristine, setIsPristine] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(true);

  const options = {
    minimap: { enabled: false },
    contextmenu: false,
    automaticLayout: true,
    scrollBeyondLastLine: false,
    extraEditorClassName: 'editor-pane',
    lineNumbers: 'off',
  } as any;

  useEffect(() => {
    const evaluate = async () => {
      try {
        const res: any = await jsonataWithCallback(expression, data);

        if (res) {
          setSaveDisabled(false);
          setResult(JSON.stringify(res, null, 2));
          isPristine && setIsPristine(false);
        }
      } catch (e: any) {
        setSaveDisabled(true);
        !isPristine && setResult(e.message);
      }
    };

    evaluate();
  }, [expression]);

  const save = () => {
    const statedExpr = '/${' + expression + '}';
    onSave(statedExpr);
  };

  return (
    <div data-testid="dashify-editor-expressions" className="h-[100%]">
      <div
        className="flex flex-row gap-2"
        style={{ height: 'calc(100% - 70px)', borderRadius: 0, boxShadow: 'none', overflow: 'auto' }}
      >
        {!isFullInspectMode && (
          <div className="w-full">
            <p className="font-bold pb-2">{ExpressionEditor.DATAHEADER}</p>
            <div style={{ height: 'calc(100% - 23px)' }}>
              <Editor className="border rounded" value={json} options={options} />
            </div>
          </div>
        )}
        {isFullInspectMode && (
          <div className="w-full flex flex-row">
            <div className="w-1/2">
              <p className="font-bold pb-2">{ExpressionEditor.DATAHEADER}</p>
              <div style={{ height: 'calc(100% - 23px)' }}>
                <Editor className="border rounded" value={json} options={options} />
              </div>
            </div>
            <div className="flex flex-col w-1/2">
              <p className="font-bold pb-2">{ExpressionEditor.EXPRESSIONHEADER}</p>
              <div style={{ height: 'calc(33% - 23px)' }}>
                <Editor
                  className="border rounded"
                  value={expression}
                  options={options}
                  onChange={e => setExpression(e ? e : '')}
                />
              </div>
              <p className="font-bold pb-2 pt-4">{ExpressionEditor.RESULTHEADER}</p>
              <div style={{ height: 'calc(100% - 23px)' }}>
                <Editor className="border rounded" value={result} options={options} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end py-2">
        {!isFullInspectMode ? (
          <Button onClick={() => setFullInspectMode(true)}>Transform Data</Button>
        ) : (
          <div className="flex gap-2">
            <Button type="SECONDARY" onClick={() => setFullInspectMode(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saveDisabled}>
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
